// locationManager.js

/** @typedef {import('./locationTargeting.js').Location} Location */

/** @typedef {import('./locationTargeting.js').Location} Location */

/**
 * Compare two values that could be strings or integers
 * @param {string|number} a
 * @param {string|number} b
 * @returns {boolean}
 */
function compareValues(a, b) {
  if (a === b) return true
  if (a == null || b == null) return false
  
  // Convert both to strings for comparison
  const aStr = String(a)
  const bStr = String(b)
  
  return aStr === bStr
}

/**
 * Check if A contains B
 * @param {Location} a
 * @param {Location} b
 * @returns {boolean}
 */
/**
 * @param {object} a
 * @param {object} b
 * @returns {boolean} true if `a` contains `b`
 */
export function locationContains(a, b) {
  if (compareValues(a.key, b.key)) return true

  if (a.is_worldwide) return true
  if (b.is_worldwide) return false

  // Country group contains countries
  if (a.type === 'country_group' && b.type === 'country') {
    return a.country_codes?.includes(b.country_code)
  }

  // Country contains smaller types in same country
  if (a.type === 'country' && compareValues(b.country_code, a.country_code)) {
    return [
      'region',
      'large_geo_area',
      'medium_geo_area',
      'small_geo_area',
      'city',
      'place',
      'geo_market',
      'subcity',
      'neighborhood',
      'electoral_district',
    ].includes(b.type)
  }

  // Region contains locations with matching region_id
  if (a.type === 'region' && b.region_id && compareValues(a.key, b.region_id)) {
    return [
      'large_geo_area',
      'medium_geo_area',
      'small_geo_area',
      'city',
      'place',
      'subcity',
      'neighborhood',
      'electoral_district',
    ].includes(b.type)
  }

  // Large geo area may contain medium/small
  if (a.type === 'large_geo_area' && b.region_id && compareValues(a.key, b.region_id)) {
    return ['medium_geo_area', 'small_geo_area'].includes(b.type)
  }

  // Medium geo area may contain small
  if (a.type === 'medium_geo_area' && b.region_id && compareValues(a.key, b.region_id)) {
    return b.type === 'small_geo_area'
  }

  // City contains subcity/neighborhood with same primary_city_id or region
  if (a.type === 'city') {
    if (b.primary_city_id && a.primary_city_id && compareValues(b.primary_city_id, a.primary_city_id)) {
      return ['subcity', 'neighborhood', 'place'].includes(b.type)
    }
    if (b.region_id && a.region_id && compareValues(b.region_id, a.region_id) && compareValues(a.key, b.primary_city_id)) {
      return ['subcity', 'neighborhood', 'place'].includes(b.type)
    }
  }

  // Subcity contains neighborhood
  if (a.type === 'subcity' && b.region_id && compareValues(a.region_id, b.region_id)) {
    return b.type === 'neighborhood' || b.type === 'place'
  }

  return false
}

/**
 * Adds a location to an existing list and removes redundant ones
 * @param {Location[]} existingLocations
 * @param {Location} newLocation
 * @returns {Location[]}
 */
export function addLocation(existingLocations, newLocation) {
  console.log('Adding location:', existingLocations, newLocation)
  return [
    ...existingLocations.filter(
      (loc) =>
        !locationContains(loc.data, newLocation.data) && // Remove container of new
        !locationContains(newLocation.data, loc.data) && // Remove contained by new
        !compareValues(loc.data.key, newLocation.data.key), // Remove exact duplicates
    ),
    newLocation,
  ]
}
